import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Send,
  MessageSquare,
  ChevronDown,
  User,
  Bot,
  Clock,
  Phone,
  MoreVertical,
  Search,
  Plus,
  Loader2,
  Trash2,
  AlertTriangle,
  X,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const CHAT_REQUEST_TIMEOUT_MS = 180000;
const CHAT_POLL_INTERVAL_MS = 10000;
const CHAT_POLL_MAX_ATTEMPTS = 18;
const HISTORY_FETCH_TIMEOUT_MS = 30000;

const isRecoverableSendError = (err) =>
  err?.code === "ECONNABORTED" || (!err?.response && err?.request);

const sendErrorStatus = (err) => err?.response?.status;

const isV2RetryableSendError = (err) => {
  const status = sendErrorStatus(err);
  return status === 503 || isRecoverableSendError(err);
};

const resolveSendErrorMessage = (err) => {
  if (err?.code === "ECONNABORTED") {
    return (
      "Response is taking longer than usual — your message may still be processing. " +
      "Wait a moment and refresh the chat."
    );
  }
  if (!err?.response && err?.request) {
    return "Connection lost — check your network and retry.";
  }
  const status = sendErrorStatus(err);
  if (status === 409 || status === 429) {
    const detail = err?.response?.data?.detail;
    return typeof detail === "string" ? detail : "Please try again.";
  }
  if (status >= 500) {
    return "Server error — please try again in a minute.";
  }
  return "Sorry, there was an error processing your message. Please try again.";
};

const parseTimestampMs = (timestamp) => {
  if (!timestamp) return 0;
  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const historyHasNewAssistantReply = (history, sentAtMs, baselineLength, userMessageText) => {
  if (!Array.isArray(history) || history.length === 0) return false;

  const last = history[history.length - 1];
  if (last?.role !== "assistant") return false;
  if (history.length <= baselineLength) return false;

  if (parseTimestampMs(last.timestamp) >= sentAtMs - 2000) return true;
  if (history.length >= baselineLength + 2) return true;

  const previous = history[history.length - 2];
  return previous?.role === "user" && previous.content === userMessageText;
};

// Generate a random phone number that doesn't exist in current chats
const generateUniqueLeadContact = (existingContacts) => {
  const generatePhoneNumber = () => {
    // Generate Australian format: +614XXXXXXXX
    const randomDigits = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');
    return `+614${randomDigits}`;
  };

  let newContact = generatePhoneNumber();
  let attempts = 0;
  const maxAttempts = 100;

  // Keep generating until we find a unique one
  while (existingContacts.includes(newContact) && attempts < maxAttempts) {
    newContact = generatePhoneNumber();
    attempts++;
  }

  return newContact;
};

const createLocalTestChat = (existingContacts) => {
  const now = new Date().toISOString();
  return {
    id: `new-chat-${Date.now()}`,
    lead_contact: generateUniqueLeadContact(existingContacts),
    platform: "test",
    ai_active: true,
    chat_history: [],
    timestamps: {
      created_at: now,
      updated_at: now,
    },
  };
};

// Format timestamp helper
const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
};

// Chat List Item Component
const ChatListItem = ({ chat, isSelected, onClick }) => {
  // Extract last message from chat_history
  const lastMessage = chat.chat_history && chat.chat_history.length > 0 
    ? chat.chat_history[chat.chat_history.length - 1] 
    : null;
  
  const lastMessageText = lastMessage?.content || "No messages yet";
  const lastMessageTime = chat.timestamps?.updated_at || lastMessage?.timestamp;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 flex items-start gap-3 transition-all text-left ${
        isSelected
          ? "bg-[#00D1FF]/10 border-l-2 border-[#00D1FF]"
          : "hover:bg-white/5 border-l-2 border-transparent"
      }`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
        <User className="w-5 h-5 text-[#9CA3AF]" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="font-body text-sm text-[#F3F4F6] font-medium">
            {chat.lead_contact || "Unknown Contact"}
          </span>
          <span className="text-xs text-[#9CA3AF]">
            {formatDate(lastMessageTime)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#00D1FF] capitalize">{chat.platform}</span>
          {chat.ai_active && (
            <span className="text-xs text-green-400">• AI Active</span>
          )}
        </div>
        <p className="text-sm text-[#9CA3AF] truncate mt-1">{lastMessageText}</p>
      </div>
    </button>
  );
};

// Message Bubble Component
const MessageBubble = ({ message }) => {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`max-w-[75%] min-w-0 break-words ${
          isUser
            ? "bg-[#00D1FF] text-[#0F1115]"
            : "bg-white/10 text-[#F3F4F6]"
        } rounded-2xl px-4 py-3 ${
          isUser ? "rounded-br-md" : "rounded-bl-md"
        }`}
      >
        <p className="text-sm font-body leading-relaxed break-words [overflow-wrap:anywhere]">{message.content}</p>
        <p
          className={`text-xs mt-2 ${
            isUser ? "text-[#0F1115]/60" : "text-[#9CA3AF]"
          }`}
        >
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

// Main Chat Test Page Component
const ChatTest = () => {
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(null); // chat_id of open menu
  const [deleteConfirm, setDeleteConfirm] = useState(null); // chat_id to confirm deletion
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isPollingForReply, setIsPollingForReply] = useState(false);
  const [pendingRefreshChatId, setPendingRefreshChatId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollSessionRef = useRef(0);
  const activePollChatRef = useRef(null);
  const v2PendingByChatRef = useRef({});

  const fetchHistoryFromApi = useCallback(async (chatId) => {
    const token = getClientAuthToken();
    if (!token || chatId.startsWith("new-chat-")) {
      return null;
    }

    const response = await axios.post(
      `${BACKEND_URL}/api/agent/v1/get-test-chat-history`,
      {
        token,
        chat_id: chatId,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: HISTORY_FETCH_TIMEOUT_MS,
      }
    );

    if (response.data.status === "ok") {
      return response.data.chat_history || [];
    }

    return null;
  }, []);

  const resolveBackendChatId = useCallback(async (leadContact) => {
    const token = getClientAuthToken();
    if (!token) return null;

    const response = await axios.get(`${BACKEND_URL}/api/agent/v1/get_test_chats`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: HISTORY_FETCH_TIMEOUT_MS,
    });

    if (response.data.status !== "ok") return null;

    const match = (response.data.chats || []).find(
      (chat) => chat.lead_contact === leadContact
    );
    return match?.id ?? null;
  }, []);

  const applyResolvedChatMessages = useCallback((chatId, history) => {
    setChatMessages((prev) => ({
      ...prev,
      [chatId]: history,
    }));

    const lastMessage = history[history.length - 1];
    setChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              chat_history: lastMessage ? [lastMessage] : chat.chat_history,
              timestamps: {
                ...chat.timestamps,
                updated_at: lastMessage?.timestamp || new Date().toISOString(),
              },
            }
          : chat
      )
    );
  }, []);

  const migrateLocalChatToBackendId = useCallback((localChatId, backendChatId) => {
    setChats((prev) => {
      const localChat = prev.find((chat) => chat.id === localChatId);
      if (!localChat) return prev;

      const withoutLocal = prev.filter((chat) => chat.id !== localChatId);
      if (withoutLocal.some((chat) => chat.id === backendChatId)) {
        return withoutLocal;
      }

      return [{ ...localChat, id: backendChatId }, ...withoutLocal];
    });

    setChatMessages((prev) => {
      const localMessages = prev[localChatId];
      if (!localMessages) return prev;

      const next = { ...prev };
      delete next[localChatId];
      if (!next[backendChatId]) {
        next[backendChatId] = localMessages;
      }
      return next;
    });

    setSelectedChatId((prev) => (prev === localChatId ? backendChatId : prev));
    activePollChatRef.current = backendChatId;
    const pending = v2PendingByChatRef.current[localChatId];
    if (pending) {
      v2PendingByChatRef.current[backendChatId] = pending;
      delete v2PendingByChatRef.current[localChatId];
    }
  }, []);

  const startPollingForReply = useCallback(
    ({ localChatId, leadContact, sentAtMs, baselineLength, userMessageText }) => {
      const sessionId = ++pollSessionRef.current;
      activePollChatRef.current = localChatId;
      setIsPollingForReply(true);
      setPendingRefreshChatId(localChatId);

      const runPollLoop = async () => {
        let resolvedChatId = localChatId.startsWith("new-chat-") ? null : localChatId;

        for (let attempt = 0; attempt < CHAT_POLL_MAX_ATTEMPTS; attempt += 1) {
          if (pollSessionRef.current !== sessionId) return;

          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, CHAT_POLL_INTERVAL_MS));
            if (pollSessionRef.current !== sessionId) return;
          }

          try {
            if (!resolvedChatId || resolvedChatId.startsWith("new-chat-")) {
              const backendChatId = await resolveBackendChatId(leadContact);
              if (!backendChatId) continue;
              resolvedChatId = backendChatId;
              migrateLocalChatToBackendId(localChatId, backendChatId);
            }

            const history = await fetchHistoryFromApi(resolvedChatId);
            if (!history) continue;

            if (
              historyHasNewAssistantReply(
                history,
                sentAtMs,
                baselineLength,
                userMessageText
              )
            ) {
              applyResolvedChatMessages(resolvedChatId, history);
              setPendingRefreshChatId(null);
              setIsPollingForReply(false);
              setIsSending(false);
              activePollChatRef.current = null;
              return;
            }
          } catch (pollErr) {
            console.error("Error polling chat history:", pollErr);
          }
        }

        if (pollSessionRef.current !== sessionId) return;

        setPendingRefreshChatId(resolvedChatId || localChatId);
        setIsPollingForReply(false);
        setIsSending(false);
        activePollChatRef.current = null;
      };

      runPollLoop();
    },
    [
      applyResolvedChatMessages,
      fetchHistoryFromApi,
      migrateLocalChatToBackendId,
      resolveBackendChatId,
    ]
  );

  const postV2Receipt = useCallback(async (outboundId, deliveryCursor, token) => {
    if (!outboundId || !deliveryCursor) return;
    for (let attempt = 0; attempt < CHAT_POLL_MAX_ATTEMPTS; attempt += 1) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/agent/v1/receipts`,
          {
            outbound_id: outboundId,
            delivery_cursor: deliveryCursor,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            timeout: HISTORY_FETCH_TIMEOUT_MS,
          }
        );
        return;
      } catch (err) {
        const status = sendErrorStatus(err);
        if (status === 403 || status === 404) return;
        const retryable = status >= 500 || isRecoverableSendError(err);
        if (!retryable || attempt === CHAT_POLL_MAX_ATTEMPTS - 1) return;
      }
    }
  }, []);

  const startV2TransitionPoll = useCallback(
    ({ chatId, transitionId }) => {
      const sessionId = ++pollSessionRef.current;
      activePollChatRef.current = chatId;
      v2PendingByChatRef.current[chatId] = { transitionId };
      setIsPollingForReply(true);
      setIsSending(true);
      setPendingRefreshChatId(chatId);

      const runPollLoop = async () => {
        const token = getClientAuthToken();
        if (!token) {
          delete v2PendingByChatRef.current[chatId];
          setIsPollingForReply(false);
          setIsSending(false);
          activePollChatRef.current = null;
          return;
        }

        for (let attempt = 0; attempt < CHAT_POLL_MAX_ATTEMPTS; attempt += 1) {
          if (pollSessionRef.current !== sessionId) return;

          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, CHAT_POLL_INTERVAL_MS));
            if (pollSessionRef.current !== sessionId) return;
          }

          try {
            const res = await axios.get(
              `${BACKEND_URL}/api/agent/v1/transitions/${encodeURIComponent(transitionId)}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                timeout: HISTORY_FETCH_TIMEOUT_MS,
              }
            );
            const dto = res.data;
            if (dto?.processing_status === "processing") {
              continue;
            }
            const replyText = dto?.response_text;
            if (typeof replyText === "string" && replyText.length > 0) {
              const aiResponse = {
                role: "assistant",
                content: replyText,
                timestamp: new Date().toISOString(),
              };
              setChatMessages((prev) => ({
                ...prev,
                [chatId]: [...(prev[chatId] || []), aiResponse],
              }));
              setChats((prev) =>
                prev.map((chat) =>
                  chat.id === chatId
                    ? {
                        ...chat,
                        chat_history: [aiResponse],
                        timestamps: {
                          ...chat.timestamps,
                          updated_at: aiResponse.timestamp,
                        },
                      }
                    : chat
                )
              );
            }
            await postV2Receipt(dto.reply_outbox_id, dto.delivery_cursor, token);
            delete v2PendingByChatRef.current[chatId];
            setPendingRefreshChatId(null);
            setIsPollingForReply(false);
            setIsSending(false);
            activePollChatRef.current = null;
            return;
          } catch (pollErr) {
            const status = sendErrorStatus(pollErr);
            if (status === 401 || status === 403 || status === 404) {
              delete v2PendingByChatRef.current[chatId];
              setIsPollingForReply(false);
              setIsSending(false);
              activePollChatRef.current = null;
              return;
            }
            console.error("Error polling v2 transition:", pollErr);
          }
        }

        if (pollSessionRef.current !== sessionId) return;

        setPendingRefreshChatId(chatId);
        setIsPollingForReply(false);
        setIsSending(false);
        activePollChatRef.current = null;
      };

      runPollLoop();
    },
    [postV2Receipt]
  );

  const cancelActivePoll = useCallback(() => {
    pollSessionRef.current += 1;
    activePollChatRef.current = null;
    setIsPollingForReply(false);
    setIsSending(false);
  }, []);

  // Fetch chat history for a specific chat
  const fetchChatHistory = useCallback(async (chatId, { force = false } = {}) => {
    // Skip if it's a new chat (not yet saved to backend)
    if (chatId.startsWith("new-chat-")) {
      return;
    }

    // Skip if this chat was already loaded ([] is truthy — do not refetch empty).
    if (!force && chatMessages[chatId]) {
      return;
    }

    const token = getClientAuthToken();
    if (!token) {
      return;
    }

    setIsLoadingHistory(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/agent/v1/get-test-chat-history`,
        {
          token: token,
          chat_id: chatId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: CHAT_REQUEST_TIMEOUT_MS,
        }
      );

      if (response.data.status === "ok") {
        const history = response.data.chat_history || [];
        setChatMessages((prev) => {
          const local = prev[chatId] || [];
          if (history.length === 0 && local.length > 0) {
            return prev;
          }
          return {
            ...prev,
            [chatId]: history,
          };
        });
        setPendingRefreshChatId((prev) => (prev === chatId ? null : prev));
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [chatMessages]);

  // Fetch test chats from API
  useEffect(() => {
    let cancelled = false;

    const fetchTestChats = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = getClientAuthToken();
        if (!token) {
          if (!cancelled) {
            setError("Not authenticated");
            setIsLoading(false);
          }
          return;
        }

        const response = await axios.get(`${BACKEND_URL}/api/agent/v1/get_test_chats`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;

        if (response.data.status === "ok") {
          const loadedChats = response.data.chats || [];
          if (loadedChats.length > 0) {
            setChats(loadedChats);
            setSelectedChatId(loadedChats[0].id);
          } else {
            const newChat = createLocalTestChat([]);
            setChats([newChat]);
            setSelectedChatId(newChat.id);
            setChatMessages((prev) => ({
              ...prev,
              [newChat.id]: [],
            }));
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching test chats:", err);
        setError("Failed to load chats");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTestChats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch chat history when selected chat changes
  useEffect(() => {
    if (selectedChatId) {
      fetchChatHistory(selectedChatId);
    }
  }, [selectedChatId, fetchChatHistory]);

  // Get current chat
  const selectedChat = chats.find((c) => c.id === selectedChatId);
  const currentMessages = chatMessages[selectedChatId] || [];

  // Filter chats based on search
  const filteredChats = chats.filter((chat) => {
    const lastMessage = chat.chat_history?.[0]?.content || "";
    return (
      chat.lead_contact?.includes(searchQuery) ||
      lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages]);

  useEffect(() => {
    return () => {
      pollSessionRef.current += 1;
    };
  }, []);

  // Handle selecting a chat
  const handleSelectChat = (chatId) => {
    if (activePollChatRef.current && activePollChatRef.current !== chatId) {
      cancelActivePoll();
    }
    setSelectedChatId(chatId);
    setIsMenuOpen(null);
    const pending = v2PendingByChatRef.current[chatId];
    if (pending?.transitionId) {
      if (activePollChatRef.current !== chatId) {
        startV2TransitionPoll({ chatId, transitionId: pending.transitionId });
      }
    } else if (selectedChatId !== chatId) {
      fetchChatHistory(chatId, { force: true });
    }
  };

  // Show delete confirmation
  const showDeleteConfirm = (chatId) => {
    setDeleteConfirm(chatId);
    setIsMenuOpen(null);
  };

  // Handle delete chat
  const handleDeleteChat = async (chatId) => {
    // If it's a new chat that hasn't been saved yet, just remove from local state
    if (chatId.startsWith("new-chat-")) {
      setChats((prev) => prev.filter((chat) => chat.id !== chatId));
      setChatMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[chatId];
        return newMessages;
      });
      
      if (selectedChatId === chatId) {
        const remainingChats = chats.filter((chat) => chat.id !== chatId);
        if (remainingChats.length > 0) {
          setSelectedChatId(remainingChats[0].id);
        } else {
          setSelectedChatId(null);
        }
      }
      setDeleteConfirm(null);
      return;
    }

    const token = getClientAuthToken();
    if (!token) {
      setError("Not authenticated");
      setDeleteConfirm(null);
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/agent/v1/delete-chat/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Remove chat from local state
      setChats((prev) => prev.filter((chat) => chat.id !== chatId));
      
      // Remove chat messages from local state
      setChatMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[chatId];
        return newMessages;
      });

      // If the deleted chat was selected, select another chat
      if (selectedChatId === chatId) {
        const remainingChats = chats.filter((chat) => chat.id !== chatId);
        if (remainingChats.length > 0) {
          setSelectedChatId(remainingChats[0].id);
        } else {
          setSelectedChatId(null);
        }
      }

      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting chat:", err);
      setError("Failed to delete chat");
      setDeleteConfirm(null);
    }
  };

  // Handle creating a new test chat
  const handleNewTestChat = () => {
    const existingContacts = chats.map((chat) => chat.lead_contact);
    const newChat = createLocalTestChat(existingContacts);

    setChats((prev) => [newChat, ...prev]);
    setSelectedChatId(newChat.id);
    setChatMessages((prev) => ({
      ...prev,
      [newChat.id]: [],
    }));
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!message.trim() || !selectedChatId || isSending) return;

    const token = getClientAuthToken();
    if (!token) {
      setError("Not authenticated");
      return;
    }

    const messageText = message.trim();
    const sendChatId = selectedChatId;
    const leadContact = selectedChat?.lead_contact || "";
    const clientMessageId = crypto.randomUUID();
    const sentAtMs = Date.now();
    const baselineLength = (chatMessages[sendChatId] || []).length;
    const newMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date(sentAtMs).toISOString(),
    };

    setChatMessages((prev) => ({
      ...prev,
      [sendChatId]: [...(prev[sendChatId] || []), newMessage],
    }));

    setMessage("");
    setIsSending(true);
    setPendingRefreshChatId(null);

    let startedPoll = false;
    let response = null;
    let lastErr = null;

    try {
      for (let attempt = 0; attempt < CHAT_POLL_MAX_ATTEMPTS; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, CHAT_POLL_INTERVAL_MS));
        }
        try {
          response = await axios.post(
            `${BACKEND_URL}/api/agent/v1/send-test-message`,
            {
              lead_contact: leadContact,
              message: messageText,
              client_message_id: clientMessageId,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              timeout: CHAT_REQUEST_TIMEOUT_MS,
            }
          );
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const status = sendErrorStatus(err);
          if (status === 409 || status === 429) {
            break;
          }
          if (!isV2RetryableSendError(err) || attempt === CHAT_POLL_MAX_ATTEMPTS - 1) {
            break;
          }
        }
      }

      if (!response) {
        throw lastErr || new Error("Failed to send message");
      }

      let targetChatId = sendChatId;
      if (targetChatId.startsWith("new-chat-")) {
        const backendChatId = await resolveBackendChatId(leadContact);
        if (backendChatId) {
          migrateLocalChatToBackendId(targetChatId, backendChatId);
          targetChatId = backendChatId;
        }
      }

      if (Boolean(response.data.transition_id)) {
        startedPoll = true;
        startV2TransitionPoll({
          chatId: targetChatId,
          transitionId: response.data.transition_id,
        });
        return;
      }

      if (typeof response.data.response_text === "string") {
        const aiResponse = {
          role: "assistant",
          content: response.data.response_text,
          timestamp: new Date().toISOString(),
        };

        setChatMessages((prev) => ({
          ...prev,
          [targetChatId]: [...(prev[targetChatId] || []), aiResponse],
        }));

        setChats((prev) =>
          prev.map((chat) =>
            chat.id === targetChatId
              ? {
                  ...chat,
                  chat_history: [aiResponse],
                  timestamps: {
                    ...chat.timestamps,
                    updated_at: new Date().toISOString(),
                  },
                }
              : chat
          )
        );
      }
    } catch (err) {
      console.error("Error sending message:", err);
      const status = sendErrorStatus(err);
      if (isRecoverableSendError(err)) {
        startedPoll = true;
        startPollingForReply({
          localChatId: sendChatId,
          leadContact,
          sentAtMs,
          baselineLength,
          userMessageText: messageText,
        });
        return;
      }
      if (status === 503) {
        startedPoll = true;
        setPendingRefreshChatId(sendChatId);
        setIsPollingForReply(false);
        setIsSending(false);
        return;
      }

      const errorText = resolveSendErrorMessage(err);
      const errorMessage = {
        role: "assistant",
        content: errorText,
        timestamp: new Date().toISOString(),
      };

      setChatMessages((prev) => ({
        ...prev,
        [sendChatId]: [...(prev[sendChatId] || []), errorMessage],
      }));
    } finally {
      if (!startedPoll) {
        setIsSending(false);
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115]">
      <Sidebar />

      {/* Main Content */}
      <div className="md:ml-64 min-h-screen">
        <div className="h-screen flex flex-col">
          {/* Header */}
          <header className="bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5 p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#00D1FF]" />
                </div>
                <div>
                  <h1 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6]">
                    Chat (Test)
                  </h1>
                  <p className="text-sm text-[#9CA3AF]">
                    Test your Digital Assistant responses
                  </p>
                </div>
              </div>
            </div>
          </header>

          {/* Chat Interface Container */}
          <div className="flex-1 flex min-w-0 overflow-hidden">
            {/* Chat List Sidebar */}
            <div className="w-80 border-r border-white/5 flex flex-col bg-[#0F1115] hidden lg:flex">
              {/* Search */}
              <div className="p-4 border-b border-white/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 text-sm"
                  />
                </div>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="p-6 text-center">
                    <Loader2 className="w-6 h-6 text-[#00D1FF] animate-spin mx-auto mb-2" />
                    <p className="text-[#9CA3AF] text-sm">Loading chats...</p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                ) : filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <ChatListItem
                      key={chat.id}
                      chat={chat}
                      isSelected={chat.id === selectedChatId}
                      onClick={() => handleSelectChat(chat.id)}
                    />
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-10 h-10 text-[#9CA3AF]/30 mx-auto mb-3" />
                    <p className="text-[#9CA3AF] text-sm">No test chats yet</p>
                    <p className="text-[#9CA3AF]/60 text-xs mt-1">
                      Start a new test chat to begin
                    </p>
                  </div>
                )}
              </div>

              {/* New Chat Button */}
              <div className="p-4 border-t border-white/5">
                <button 
                  onClick={handleNewTestChat}
                  className="w-full flex items-center justify-center gap-2 bg-[#00D1FF]/10 hover:bg-[#00D1FF]/20 border border-[#00D1FF]/30 text-[#00D1FF] rounded-lg py-2.5 px-4 transition-all text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  New Test Chat
                </button>
              </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Mobile Chat Selector Dropdown */}
              <div className="lg:hidden border-b border-white/5 p-4">
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#9CA3AF]" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium">
                          {selectedChat?.lead_contact || "Select a chat"}
                        </p>
                        <p className="text-xs text-[#00D1FF] capitalize">
                          {selectedChat?.platform}
                        </p>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-[#9CA3AF] transition-transform ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1d21] border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                      {/* New Chat Button in Dropdown */}
                      <button
                        onClick={() => {
                          handleNewTestChat();
                          setIsDropdownOpen(false);
                        }}
                        className="w-full p-3 flex items-center gap-3 text-left hover:bg-white/5 border-b border-white/10 text-[#00D1FF]"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#00D1FF]/10 flex items-center justify-center">
                          <Plus className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium">New Test Chat</span>
                      </button>
                      {chats.map((chat) => (
                        <button
                          key={chat.id}
                          onClick={() => {
                            handleSelectChat(chat.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full p-3 flex items-center gap-3 text-left hover:bg-white/5 ${
                            chat.id === selectedChatId ? "bg-[#00D1FF]/10" : ""
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-[#9CA3AF]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#F3F4F6]">
                              {chat.lead_contact}
                            </p>
                            <p className="text-xs text-[#9CA3AF] truncate">
                              {chat.chat_history?.[0]?.content || "No messages"}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Header */}
              {selectedChat && (
                <div className="hidden lg:flex items-center justify-between px-6 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#9CA3AF]" />
                    </div>
                    <div>
                      <h2 className="font-heading font-medium text-[#F3F4F6]">
                        {selectedChat.lead_contact}
                      </h2>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#00D1FF] capitalize">
                          {selectedChat.platform}
                        </span>
                        <span className="text-xs text-[#9CA3AF]">•</span>
                        <span className="text-xs text-[#9CA3AF]">
                          Last active {formatDate(selectedChat.timestamps?.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* <button className="p-2 rounded-lg hover:bg-white/5 text-[#9CA3AF] hover:text-[#F3F4F6] transition-all">
                      <Phone className="w-5 h-5" />
                    </button> */}
                    <div className="relative">
                      <button 
                        onClick={() => setIsMenuOpen(isMenuOpen === selectedChat.id ? null : selectedChat.id)}
                        className="p-2 rounded-lg hover:bg-white/5 text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {isMenuOpen === selectedChat.id && (
                        <div className="absolute right-0 top-full mt-1 bg-[#1a1d21] border border-white/10 rounded-lg shadow-xl z-50 min-w-[150px]">
                          <button
                            onClick={() => showDeleteConfirm(selectedChat.id)}
                            className="w-full px-4 py-2.5 flex items-center gap-2 text-left text-red-400 hover:bg-white/5 rounded-lg transition-all text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Chat
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Area */}
              <div className="flex-1 min-w-0 overflow-y-auto p-4 md:p-6 space-y-4">
                {isLoadingHistory ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-[#00D1FF] animate-spin mx-auto mb-3" />
                      <p className="text-[#9CA3AF] text-sm">Loading chat history...</p>
                    </div>
                  </div>
                ) : currentMessages.length > 0 ? (
                  <>
                    {currentMessages.map((msg, index) => (
                      <MessageBubble key={index} message={msg} />
                    ))}
                    {isSending && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#00D1FF]/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-[#00D1FF]" />
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 max-w-[80%]">
                          <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                            <Loader2 className="w-4 h-4 animate-spin text-[#00D1FF]" />
                            {isPollingForReply
                              ? "Waiting for AI reply…"
                              : "Processing (this can take up to 2 minutes)…"}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-[#9CA3AF]" />
                      </div>
                      <p className="text-[#9CA3AF] text-sm">
                        {chats.length === 0 
                          ? "No test chats yet. Create a new one to start testing!"
                          : "No messages yet. Start the conversation!"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="border-t border-white/5 p-4 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      rows={1}
                      disabled={isSending}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 resize-none font-body text-sm disabled:opacity-50"
                      style={{ minHeight: "48px", maxHeight: "120px" }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isSending}
                    className={`p-3 rounded-xl transition-all ${
                      message.trim() && !isSending
                        ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                        : "bg-white/10 text-[#9CA3AF] cursor-not-allowed"
                    }`}
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-[#9CA3AF]/60 mt-2 text-center">
                  {isSending
                    ? isPollingForReply
                      ? "Waiting for AI reply — checking every 10 seconds"
                      : "AI is thinking — long replies can take up to 2 minutes"
                    : pendingRefreshChatId === selectedChatId
                      ? (
                          <span>
                            Reply is still processing.{" "}
                            <button
                              type="button"
                              onClick={() => fetchChatHistory(selectedChatId, { force: true })}
                              className="text-[#00D1FF] hover:underline"
                            >
                              Refresh chat
                            </button>
                          </span>
                        )
                      : "Press Enter to send • This is a test environment"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-[#1a1d21] border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-[#F3F4F6]">Delete Chat</h3>
                <p className="text-sm text-[#9CA3AF]">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-[#9CA3AF] text-sm mb-6">
              Are you sure you want to delete this chat? All messages will be permanently removed.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[#F3F4F6] text-sm font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteChat(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTest;
